import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { MapPin, House, SealCheck, ShieldCheck, Calendar, Users, ForkKnife, Snowflake, WifiHigh } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as UiCalendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function RentalDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [months, setMonths] = useState(6);
  const [applying, setApplying] = useState(false);

  useEffect(() => { api.get(`/properties/${id}`).then(({ data }) => setProp(data)); }, [id]);

  if (!prop) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const rentTotal = months * prop.monthly_rent;
  const platformFee = Math.round(rentTotal * 0.02);
  const total = rentTotal + prop.security_deposit + platformFee;
  const availableMonths = Array.from({ length: prop.max_lease_months - prop.lock_in_months + 1 },
                                      (_, i) => prop.lock_in_months + i);

  const apply = async () => {
    if (!user) { toast.info("Please sign in first"); nav("/otp-login"); return; }
    if (!user.is_verified) {
      toast.info("Complete ID verification before renting");
      nav("/verification");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/rentals", {
        property_id: prop.id, start_date: startDate.toISOString(), lease_months: months,
      });
      toast.success("Rental request created — proceed to payment");
      nav(`/rental-checkout/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="grid grid-cols-12 grid-rows-2 gap-3 h-[60vh] mb-10 rounded-2xl overflow-hidden">
        <img src={prop.images[0]} alt="" className="col-span-12 md:col-span-8 row-span-2 w-full h-full object-cover" data-testid="rental-hero-img" />
        {prop.images.slice(1, 3).map((src, i) => (
          <img key={i} src={src} alt="" className="hidden md:block col-span-4 row-span-1 w-full h-full object-cover" />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-3">
            <p className="text-xs tracking-[0.3em] uppercase text-gold">{prop.room_type} · {prop.furnishing}</p>
            {prop.owner?.is_verified && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1">
                <SealCheck size={12} weight="fill" /> Verified Owner
              </span>
            )}
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-4" data-testid="rental-title">{prop.title}</h1>
          <div className="flex items-center gap-4 mb-6 text-sm text-neutral-600 flex-wrap">
            <span className="flex items-center gap-1"><MapPin size={16} weight="duotone" className="text-gold" /> {prop.address}</span>
          </div>
          <p className="text-neutral-700 leading-relaxed mb-10">{prop.description}</p>

          <div className="grid grid-cols-2 gap-4 mb-10 p-6 bg-stone rounded-2xl">
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500">Preferred tenant</p>
              <p className="text-midnight font-medium">{prop.preferred_tenant}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500">Available from</p>
              <p className="text-midnight font-medium">{prop.available_from || "Immediate"}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500">Lock-in</p>
              <p className="text-midnight font-medium">{prop.lock_in_months} months</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500">Max lease</p>
              <p className="text-midnight font-medium">{prop.max_lease_months} months</p>
            </div>
          </div>

          <h3 className="font-serif text-2xl mb-4 text-midnight">Amenities</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
            {prop.amenities.map((a) => (
              <div key={a} className="flex items-center gap-2 text-neutral-700 text-sm p-3 bg-white border border-black/5 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-gold" /> {a}
              </div>
            ))}
          </div>

          {prop.owner && (
            <div className="p-6 bg-white border border-black/5 rounded-2xl">
              <p className="text-xs tracking-widest uppercase text-gold mb-2">Listed by</p>
              <p className="font-serif text-xl text-midnight flex items-center gap-2">
                {prop.owner.name || "Zyro Verified"}
                {prop.owner.is_verified && <SealCheck size={16} weight="fill" className="text-emerald-600" />}
              </p>
            </div>
          )}
        </div>

        <aside>
          <div className="sticky top-28 bg-white border border-black/10 rounded-2xl p-6 shadow-lg" data-testid="rental-widget">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-serif text-3xl text-midnight">₹{prop.monthly_rent.toLocaleString("en-IN")}</span>
              <span className="text-sm text-neutral-500">/ month</span>
            </div>
            <p className="text-xs text-neutral-500 mb-6">+ ₹{prop.security_deposit.toLocaleString("en-IN")} refundable deposit</p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Move-in date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="w-full text-left border border-black/10 rounded-xl px-3 py-2 hover:bg-stone" data-testid="rental-startdate-btn">
                      {format(startDate, "dd MMM yyyy")}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <UiCalendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)}
                      disabled={(d) => d < new Date(new Date().toDateString())} />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-2">Lease duration</label>
                <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                  <SelectTrigger data-testid="lease-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{availableMonths.map((m) => <SelectItem key={m} value={String(m)}>{m} months</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 pb-4 mb-4 border-b border-black/10 text-sm">
              <div className="flex justify-between text-neutral-600"><span>Rent × {months} months</span><span>₹{rentTotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Security deposit</span><span>₹{prop.security_deposit.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-neutral-600"><span>Platform fee (2%)</span><span>₹{platformFee.toLocaleString("en-IN")}</span></div>
            </div>
            <div className="flex justify-between mb-6">
              <span className="font-serif text-lg text-midnight">Payable now</span>
              <span className="font-serif text-2xl text-midnight" data-testid="rental-total">₹{total.toLocaleString("en-IN")}</span>
            </div>

            <Button onClick={apply} disabled={applying} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="apply-rent-btn">
              {applying ? "Processing..." : "Apply & Pay"}
            </Button>
            <p className="text-[11px] text-center text-neutral-500 mt-3 flex items-center justify-center gap-1">
              <ShieldCheck size={12} weight="fill" className="text-emerald-600" /> Deposit held by Zyro until check-in
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
